import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Provider, LoginCredentials, AuthContextType } from '../types';
import { authAPI } from '../services/api';
import { TokenManager } from '../utils/tokenManager';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [provider, setProvider] = useState<Provider | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const currentProviderResponse = await authAPI.getCurrentProvider();
          // Handle the /me endpoint response structure
          const providerData: Provider = {
            user_id: currentProviderResponse.user_id,
            user_email: currentProviderResponse.user_email,
            provider_id: currentProviderResponse.provider_id,
            admin_flag: currentProviderResponse.admin_flag,
            is_active: currentProviderResponse.is_active,
            is_verified: currentProviderResponse.is_verified,
            email: currentProviderResponse.email || currentProviderResponse.user_email,
            first_name: currentProviderResponse.first_name || '',
            last_name: currentProviderResponse.last_name || ''
          };
          setProvider(providerData);
          
          // Start token monitoring for authenticated users
          TokenManager.startTokenMonitoring();
        } catch (error) {
          console.error('Failed to get current provider:', error);
          // Clear invalid token and redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('token_expires_at');
          setProvider(null);
          // Stop token monitoring for unauthenticated users
          TokenManager.stopTokenMonitoring();
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
      } else {
        // Stop token monitoring for unauthenticated users
        TokenManager.stopTokenMonitoring();
      }
      setIsLoading(false);
    };

    initializeAuth();
    
    // Cleanup token monitoring on unmount
    return () => {
      TokenManager.stopTokenMonitoring();
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      console.log('AuthContext: Starting login process...');
      const response = await authAPI.login(credentials);
      console.log('AuthContext: Full login response:', response);
      
      // Handle different possible response structures
      let token: string;
      let providerData: Provider;
      
      // Handle the server response structure: { access_token, user_id, provider_id, etc. }
      if (response.access_token) {
        token = response.access_token;
        // Create provider data from server response
        providerData = {
          user_id: response.user_id,
          user_email: response.user_email,
          provider_id: response.provider_id,
          admin_flag: response.admin_flag,
          is_active: response.is_active,
          is_verified: response.is_verified,
          email: response.user_email,
          first_name: response.provider_info?.first_name || '',
          last_name: response.provider_info?.last_name || ''
        };
      } else if (response.token && response.provider) {
        // Handle alternative structure: { token, provider }
        token = response.token;
        providerData = response.provider;
      } else if (response.token && response.user) {
        // Handle another alternative: { token, user }
        token = response.token;
        providerData = response.user;
      } else {
        // If we can't determine the structure, try to extract what we can
        console.warn('AuthContext: Unexpected response structure:', response);
        token = response.token || response.access_token || '';
        providerData = response.provider || response.user || response;
      }
      
      console.log('AuthContext: Extracted token:', token);
      console.log('AuthContext: Extracted provider:', providerData);
      
      if (!token) {
        throw new Error('No token received from login');
      }
      
      if (!providerData) {
        throw new Error('No provider data received from login');
      }
      
      localStorage.setItem('authToken', token);
      if (response.expiration) {
        localStorage.setItem(
          'token_expires_at',
          new Date(Date.now() + response.expiration * 1000).toISOString()
        );
      }
      setProvider(providerData);
      
      // Start token monitoring after successful login
      TokenManager.startTokenMonitoring();
      
      console.log('AuthContext: Provider state updated, isAuthenticated should be true');
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token_expires_at');
    setProvider(null);
    setError(null);
    // Stop token monitoring when logging out
    TokenManager.stopTokenMonitoring();
  };

  const value: AuthContextType = {
    provider,
    login,
    logout,
    isLoading,
    isAuthenticated: !!provider,
    error,
  };

  console.log('AuthContext: Current state:', {
    provider,
    isLoading,
    isAuthenticated: !!provider,
    error
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
